import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  "6LdwnIItAAAAAJzWPi00oUyc38jrJpMsgJTsY8lv";

/** `YYYY-MM-DD` → true if Saturday (6) or Sunday (0) in local calendar */
function isWeekendYmd(ymd: string): boolean {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return false;
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
}

/** 30-minute slots from 8:00 AM through 5:00 PM (last slot 5:00 PM). */
const PREFERRED_TIME_OPTIONS: { value: string; label: string }[] = (() => {
  const opts: { value: string; label: string }[] = [];
  for (let h = 8; h <= 17; h++) {
    for (const mins of [0, 30]) {
      if (h === 17 && mins === 30) break;
      const hourStr = h.toString().padStart(2, "0");
      const value = `${hourStr}:${mins === 0 ? "00" : "30"}`;
      const displayHour =
        h === 0 ? 12 : h > 12 ? h - 12 : h === 12 ? 12 : h;
      const ampm = h >= 12 ? "PM" : "AM";
      const mm = mins === 0 ? "00" : "30";
      opts.push({ value, label: `${displayHour}:${mm} ${ampm}` });
    }
  }
  return opts;
})();

const ALLOWED_PREFERRED_TIMES = new Set(
  PREFERRED_TIME_OPTIONS.map((o) => o.value)
);

/**
 * Preferred date `YYYY-MM-DD` + time `HH:mm` → ISO for API `date`.
 * Uses UTC components so `08:00` becomes `…T08:00:00.000Z`, not shifted from local TZ.
 */
function toIsoFromPreferredDateAndTime(
  preferredDate: string,
  preferredTime: string
): string | null {
  const ymd = preferredDate?.trim();
  const hm = preferredTime?.trim();
  if (!ymd || !hm) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const [hh, mm] = hm.split(":").map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  const ms = Date.UTC(y, m - 1, d, hh, mm, 0, 0);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toISOString();
}

const Contact: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isPricingPage = searchParams.get("pricing") === "true";
  const isScheduleDemoSource =
    searchParams.get("source") === "schedule-demo" ||
    searchParams.get("scheduleDemo") === "true" ||
    searchParams.get("demo") === "true";
  const isContactPage = window.location.pathname === "/contact";

  const [form, setForm] = useState({
    name: "",
    title: "",
    email: "",
    phone: "",
    facilitySize: "",
    facilityAddress: "",
    city: "",
    state: "",
    zipCode: "",
    preferredDate: "",
    preferredTime: "",
    contactOption: "date", // "date" or "call"
    message: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [showCaptchaDialog, setShowCaptchaDialog] = useState(false);
  const captchaRef = useRef<ReCAPTCHA>(null);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handlePreferredDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value && isWeekendYmd(value)) {
      setForm({ ...form, preferredDate: "" });
      setErrors((prev) => ({
        ...prev,
        preferredDate:
          "Saturday and Sunday are not available. Please choose a weekday.",
      }));
      return;
    }
    setForm({ ...form, preferredDate: value });
    setErrors((prev) => ({ ...prev, preferredDate: "" }));
  };

  // Validation function
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.title.trim()) newErrors.title = "Title is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Invalid email format";

    if (!form.facilitySize.trim())
      newErrors.facilitySize = "Facility size is required";

    if (!form.facilityAddress.trim())
      newErrors.facilityAddress = "Facility address is required";

    if (!form.city.trim()) newErrors.city = "City is required";
    if (!form.state.trim()) newErrors.state = "State is required";
    if (!form.zipCode.trim()) newErrors.zipCode = "ZIP code is required";
    else if (!/^\d{5}(-\d{4})?$/.test(form.zipCode))
      newErrors.zipCode = "Invalid ZIP code format";

    if (!form.phone.trim()) newErrors.phone = "Phone number is required";

    if (
      (isPricingPage || isScheduleDemoSource) &&
      form.contactOption === "date"
    ) {
      if (!form.preferredDate.trim())
        newErrors.preferredDate = "Please choose a preferred date";
      else if (isWeekendYmd(form.preferredDate))
        newErrors.preferredDate =
          "Saturday and Sunday are not available. Please choose a weekday.";
      if (!form.preferredTime.trim())
        newErrors.preferredTime = "Please choose a preferred time";
      else if (!ALLOWED_PREFERRED_TIMES.has(form.preferredTime))
        newErrors.preferredTime =
          "Please choose a time between 8:00 AM and 5:00 PM.";
    }

    if (!form.message.trim()) newErrors.message = "Message is required";

    return newErrors;
  };

  const submitContact = async (recaptchaToken: string) => {
    setLoading(true);
    setApiMessage(null);

    const scheduleSlot =
      (isPricingPage || isScheduleDemoSource) &&
      form.contactOption === "date" &&
      (form.preferredDate || form.preferredTime)
        ? `Preferred demo date: ${form.preferredDate}. Preferred time: ${form.preferredTime}.`
        : "";
    const messageForApi = scheduleSlot
      ? `${scheduleSlot}\n\n${form.message}`
      : form.message;

    const preferredSlotIso =
      (isPricingPage || isScheduleDemoSource) &&
      form.contactOption === "date" &&
      form.preferredDate.trim() &&
      form.preferredTime.trim()
        ? toIsoFromPreferredDateAndTime(
            form.preferredDate,
            form.preferredTime
          )
        : null;
    const dateForApi = preferredSlotIso ?? new Date().toISOString();

    try {
      const response = await fetch(
        "https://apis.roomrounds.com/api/ContactUs/sendContactUsEmail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", accept: "*/*" },
          body: JSON.stringify({
            title: form.title,
            name: form.name,
            email: form.email,
            phoneNumber: form.phone,
            facilitySize: form.facilitySize,
            facilityAddress: `${form.facilityAddress}, ${form.city}, ${form.state} ${form.zipCode}`.trim(),
            date: dateForApi,
            message: messageForApi,
            recaptchaToken,
          }),
        }
      );

      if (response.ok) {
        setSubmitted(true);
        setShowCaptchaDialog(false);
        setApiMessage("Thank you! We'll be in touch soon.");
        setForm({
          name: "",
          title: "",
          email: "",
          phone: "",
          facilitySize: "",
          facilityAddress: "",
          city: "",
          state: "",
          zipCode: "",
          preferredDate: "",
          preferredTime: "",
          contactOption: "date",
          message: "",
        });
      } else {
        const errorText = await response.text();
        setSubmitted(false);
        setShowCaptchaDialog(false);
        setApiMessage(`Failed to send: ${errorText}`);
        captchaRef.current?.reset();
      }
    } catch {
      setSubmitted(false);
      setShowCaptchaDialog(false);
      setApiMessage("An error occurred. Please try again later.");
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage(null);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setShowCaptchaDialog(true);
  };

  const handleCaptchaChange = (token: string | null) => {
    if (!token) return;
    void submitContact(token);
  };

  const closeCaptchaDialog = () => {
    if (loading) return;
    setShowCaptchaDialog(false);
    captchaRef.current?.reset();
  };

  return (
    <section className="max-w-xl mx-auto py-12 px-4">
      <h2 className="text-3xl font-bold mb-6 text-center">
        {isPricingPage
          ? "Schedule a time"
          : isScheduleDemoSource
            ? "Schedule a Demo"
            : "Contact Us"}
      </h2>

      {!submitted && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-4"
        >
          {isPricingPage && (
            <p className="text-sm text-center text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-3 leading-snug">
              Custom quotes available, including 10% savings on 3-year prepay.
            </p>
          )}
          {/* Info Box */}
          {/* <div className="mb-4 p-4 bg-blue-50 rounded text-blue-800 text-center">
            {isPricingPage ? (
              <>
                <h3 className="text-xl font-semibold mb-2">
                  Contact your RoomRounds sales representative for pricing
                </h3>
                <p>
                  Reach out to discuss custom pricing options tailored to your
                  facility size and needs. Our sales team will provide you with
                  a personalized quote.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold mb-2">
                  Schedule a Personalized Demo
                </h3>
                <p>
                  Book a live walkthrough with our product specialist. Demos
                  typically last 20–30 minutes and cover features, workflows,
                  and Q&amp;A. Choose your preferred date/time below, or{" "}
                  <a href="tel:+1234567890" className="text-blue-600 underline">
                    call us
                  </a>{" "}
                  for instant scheduling.
                </p>
              </>
            )}
          </div> */}

          {/* Name */}
          <div>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={form.name}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Title */}
          <div>
            <input
              type="text"
              name="title"
              placeholder="Title"
              value={form.title}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.title && (
              <p className="text-red-600 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={form.email}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Facility Size */}
          <div>
            <input
              type="text"
              name="facilitySize"
              placeholder="Facility Size (e.g., 100 beds)"
              value={form.facilitySize}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.facilitySize && (
              <p className="text-red-600 text-sm mt-1">{errors.facilitySize}</p>
            )}
          </div>

          {/* Facility Address */}
          <div>
            <input
              type="text"
              name="facilityAddress"
              placeholder="Facility Address"
              value={form.facilityAddress}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.facilityAddress && (
              <p className="text-red-600 text-sm mt-1">
                {errors.facilityAddress}
              </p>
            )}
          </div>

          {/* City */}
          <div>
            <input
              type="text"
              name="city"
              placeholder="City"
              value={form.city}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.city && (
              <p className="text-red-600 text-sm mt-1">{errors.city}</p>
            )}
          </div>

          {/* State */}
          <div>
            <input
              type="text"
              name="state"
              placeholder="State"
              value={form.state}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.state && (
              <p className="text-red-600 text-sm mt-1">{errors.state}</p>
            )}
          </div>

          {/* ZIP Code */}
          <div>
            <input
              type="text"
              name="zipCode"
              placeholder="ZIP Code"
              value={form.zipCode}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.zipCode && (
              <p className="text-red-600 text-sm mt-1">{errors.zipCode}</p>
            )}
          </div>

          {/* Phone Number */}
          {(form.contactOption === "call" || isContactPage) && (
            <div>
              <input
                type="tel"
                name="phone"
                placeholder="Your Phone Number"
                value={form.phone}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
              {errors.phone && (
                <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          )}

          {/* Preferred demo date & time (pricing “schedule a time” + schedule demo) */}
          {form.contactOption === "date" &&
            (isPricingPage || isScheduleDemoSource) && (
              <>
                <div>
                  <label
                    htmlFor="preferredDate"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Preferred date
                  </label>
                  <p className="text-xs text-gray-500 mb-1">
                    Weekdays only (Monday–Friday). Saturday and Sunday cannot be
                    selected.
                  </p>
                  <input
                    id="preferredDate"
                    type="date"
                    name="preferredDate"
                    value={form.preferredDate}
                    onChange={handlePreferredDateChange}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full border rounded px-3 py-2"
                  />
                  {errors.preferredDate && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.preferredDate}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="preferredTime"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Preferred time
                  </label>
                  <p className="text-xs text-gray-500 mb-1">
                    Available slots: 8:00 AM – 5:00 PM.
                  </p>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={form.preferredTime}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">Select time</option>
                    {PREFERRED_TIME_OPTIONS.map((time) => (
                      <option key={time.value} value={time.value}>
                        {time.label}
                      </option>
                    ))}
                  </select>

                  {errors.preferredTime && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.preferredTime}
                    </p>
                  )}
                </div>
              </>
            )}

          {/* Message */}
          <div>
            <textarea
              name="message"
              placeholder="Your Message"
              value={form.message}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              rows={4}
            />
            {errors.message && (
              <p className="text-red-600 text-sm mt-1">{errors.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`bg-blue-600 text-white px-4 py-2 rounded w-full ${
              loading ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      )}

      {showCaptchaDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={closeCaptchaDialog}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="captcha-dialog-title"
          >
            <button
              type="button"
              onClick={closeCaptchaDialog}
              disabled={loading}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 text-xl leading-none disabled:opacity-50"
              aria-label="Close"
            >
              ×
            </button>

            <h3
              id="captcha-dialog-title"
              className="text-xl font-semibold text-center mb-2"
            >
              Security Verification
            </h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              Please verify you are not a robot to continue.
            </p>

            <div className="flex justify-center min-h-[78px]">
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
              />
            </div>

            {loading && (
              <p className="text-sm text-blue-600 text-center mt-4">
                Submitting your request...
              </p>
            )}
          </div>
        </div>
      )}

      {apiMessage && (
        <div
          className={`relative p-4 pr-10 rounded text-center mt-4 ${
            submitted
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {apiMessage}
          <button
            type="button"
            onClick={() => setApiMessage(null)}
            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded hover:bg-black/10 text-lg leading-none"
            aria-label="Close message"
          >
            ×
          </button>
        </div>
      )}

      {/* Contact Info */}
      <div className="mt-8 text-center text-gray-600">
        <div>
          Email:{" "}
          <a
            href="mailto:sales@Roomrounds.com"
            className="text-blue-600 hover:underline"
          >
            sales@Roomrounds.com
          </a>
        </div>
        {/* <div>
          Phone:{" "}
          <a href="tel:+1234567890" className="text-blue-600 hover:underline">
            +1 (234) 567-890
          </a>
        </div> */}
      </div>
    </section>
  );
};

export default Contact;
